/**
 * Module dependencies.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Animation scroll range.
 */

const ANIMATION_SCROLL_RANGE = 500;

/**
 * Element references interface.
 */

interface ElementRefs {
  background: HTMLElement | null;
  carBlueMask: HTMLElement | null;
  carWhite: HTMLElement | null;
  heroCtaActions: HTMLElement | null;
  logoClipRect: SVGRectElement | null;
  priceTag: HTMLElement | null;
  productSection: HTMLElement | null;
  stepperSection: HTMLElement | null;
}

/**
 * `ScrollAnimation` component.
 */

export function ScrollAnimation() {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const elementRefsRef = useRef<ElementRefs>({
    background: null,
    carBlueMask: null,
    carWhite: null,
    heroCtaActions: null,
    logoClipRect: null,
    priceTag: null,
    productSection: null,
    stepperSection: null
  });

  useEffect(() => {
    let scrollAccumulator = 0;
    let touchStartY = 0;

    const applyAnimations = (accumulator: number) => {
      if (accumulator < 0) {
        accumulator = 0;
      } else if (accumulator > ANIMATION_SCROLL_RANGE) {
        accumulator = ANIMATION_SCROLL_RANGE;
        setIsAnimationComplete(true);
      }

      scrollAccumulator = accumulator;

      const progress = accumulator / ANIMATION_SCROLL_RANGE;
      const { current: refs } = elementRefsRef;

      if (refs.carWhite) {
        refs.carWhite.style.opacity = `${progress}`;
      }

      if (refs.background) {
        const translateY = -100 + progress * 100;
        refs.background.style.transform = `translateY(${translateY}%)`;
      }

      if (refs.carBlueMask) {
        const translateY = -100 + progress * 100;
        refs.carBlueMask.style.transform = `translateY(${translateY}%)`;
      }

      if (refs.priceTag) {
        const translateYBox = 150 - progress * 150;
        const opacity = progress;
        refs.priceTag.style.transform = `translateY(${translateYBox}px)`;
        refs.priceTag.style.opacity = `${opacity}`;
      }

      if (refs.heroCtaActions) {
        const translateYBtn = 150 - progress * 150;
        const opacity = progress;
        refs.heroCtaActions.style.transform = `translateY(${translateYBtn}px)`;
        refs.heroCtaActions.style.opacity = `${opacity}`;
      }

      if (refs.logoClipRect) {
        const clipHeight = (scrollAccumulator - 16) * 1.3;
        refs.logoClipRect.setAttribute('height', clipHeight.toString());
      }

      if (scrollAccumulator <= 10) {
        if (refs.stepperSection) {
          refs.stepperSection.style.opacity = '0';
          refs.stepperSection.style.transform = 'translateY(250px)';
        }

        if (refs.productSection) {
          refs.productSection.style.opacity = '0';
          refs.productSection.style.transform = 'translateY(400px)';
        }
      } else {
        const fadeProgress = Math.min(1, (scrollAccumulator - 10) / 490);

        if (refs.stepperSection) {
          const translateYStepper = 250 - fadeProgress * 250;
          refs.stepperSection.style.opacity = `${fadeProgress}`;
          refs.stepperSection.style.transform = `translateY(${translateYStepper}px)`;
        }

        if (refs.productSection) {
          const translateYProduct = 400 - fadeProgress * 400;
          refs.productSection.style.opacity = `${fadeProgress}`;
          refs.productSection.style.transform = `translateY(${translateYProduct}px)`;
        }
      }
    };

    const handleWheel = (event: WheelEvent) => {
      if (isAnimationComplete) return;
      event.preventDefault();
      applyAnimations(scrollAccumulator + event.deltaY);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (isAnimationComplete) return;
      touchStartY = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (isAnimationComplete) return;
      event.preventDefault();

      const touchCurrentY = event.touches[0].clientY;
      const deltaY = (touchStartY - touchCurrentY) * 2;
      touchStartY = touchCurrentY;

      applyAnimations(scrollAccumulator + deltaY);
    };

    // Detect initial scroll position on page load/refresh
    const currentScroll = window.scrollY || window.pageYOffset;

    if (currentScroll > 0) {
      // Map the actual scroll position to our animation range
      const initialPosition = Math.min(currentScroll, ANIMATION_SCROLL_RANGE);
      scrollAccumulator = initialPosition;

      // Apply animations immediately based on initial scroll position
      setTimeout(() => {
        applyAnimations(initialPosition);
      }, 50);
    }

    if (!isAnimationComplete) {
      window.addEventListener('wheel', handleWheel, { passive: false });
      window.addEventListener('touchstart', handleTouchStart, { passive: false });
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
    }

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isAnimationComplete]);

  useEffect(() => {
    // Cache element references
    const refs = elementRefsRef.current;
    refs.carWhite = document.getElementById('hero-car-white');
    refs.background = document.getElementById('hero-background');
    refs.carBlueMask = document.getElementById('hero-car-blue-mask');
    refs.priceTag = document.getElementById('price-tag');
    refs.heroCtaActions = document.getElementById('hero-cta-actions');
    refs.logoClipRect = document.getElementById('logo-clip-rect') as SVGRectElement | null;
    refs.stepperSection = document.getElementById('section-stepper');
    refs.productSection = document.getElementById('section-produto');

    // Initialize styles
    if (refs.carWhite) {
      refs.carWhite.style.opacity = '0';
      refs.carWhite.style.transition = 'opacity 0.1s ease-out';
    }

    if (refs.background) {
      refs.background.style.transform = 'translateY(-100%)';
      refs.background.style.transition = 'transform 0.1s ease-out';
    }

    if (refs.carBlueMask) {
      refs.carBlueMask.style.transform = 'translateY(-100%)';
      refs.carBlueMask.style.transition = 'transform 0.1s ease-out';
    }

    if (refs.priceTag) {
      refs.priceTag.style.transform = 'translateY(150px)';
      refs.priceTag.style.opacity = '0';
      refs.priceTag.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out';
    }

    if (refs.heroCtaActions) {
      refs.heroCtaActions.style.transform = 'translateY(150px)';
      refs.heroCtaActions.style.opacity = '0';
      refs.heroCtaActions.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out';
    }

    if (refs.logoClipRect) {
      refs.logoClipRect.setAttribute('height', '0');
    }

    if (refs.stepperSection) {
      refs.stepperSection.style.opacity = '0';
      refs.stepperSection.style.transform = 'translateY(250px)';
      refs.stepperSection.style.transition = 'opacity 0.1s ease-out, transform 0.1s ease-out';
    }

    if (refs.productSection) {
      refs.productSection.style.opacity = '0';
      refs.productSection.style.transform = 'translateY(400px)';
      refs.productSection.style.transition = 'opacity 0.1s ease-out, transform 0.1s ease-out';
    }
  }, []);

  return null;
}
